import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import DepotInjectionForm from "../DepotInjectionChart/DepotInjectionForm";
import { createDepotInjectionChart } from "../../../store/slices/depotInjectionChartsSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";

const AddDepotInjection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading } = useSelector((state) => state.depotInjectionCharts || {});
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const [formData, setFormData] = useState({
    patientId, // initialize
    caseId,    // initialize
    name: "",
    mrNo: "",
    dob: "",
    roomNo: "",
    documentName: "Depot and Other Injection Chart",
    effectiveDate: "",
    approvedBy: "",
    approvedDate: "",
    nextRevisionBy: "",
    nextRevisionDate: "",
    injectionRecords: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (patientId) {
      dispatch(fetchPatient(patientId))
        .unwrap()
        .then((patient) => {
          if (patient) {
            setFormData((prev) => ({
              ...prev,
              name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
              dob: patient.dob ? patient.dob.split('T')[0] : "",
              mrNo: patient.mr_no || "",
            }));
          }
        })
        .catch((err) => console.error("Failed to fetch patient details:", err));
    }
  }, [dispatch, patientId]);

  // 🐍 Convert camelCase to snake_case before sending to backend
  const toSnakeCase = (data) => {
    if (Array.isArray(data)) {
      return data.map((item) => toSnakeCase(item));
    } else if (typeof data === "object" && data !== null) {
      return Object.fromEntries(
        Object.entries(data).map(([key, value]) => [
          key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`),
          toSnakeCase(value),
        ])
      );
    }
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Basic frontend validation
    if (!formData.name || !formData.mrNo) {
      setErrors({
        name: !formData.name ? "Name is required." : "",
        mrNo: !formData.mrNo ? "MR No. is required." : "",
      });
      Swal.fire({
        title: "Missing Fields",
        text: "Please fill in all required fields before submitting.",
        icon: "warning",
      });
      return;
    }

    try {
      const snakeData = toSnakeCase(formData);
      const resultAction = await dispatch(createDepotInjectionChart(snakeData));

      if (createDepotInjectionChart.fulfilled.match(resultAction)) {
        Swal.fire({
          title: "Success!",
          text: "Depot Injection Chart created successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        const res = resultAction.payload || resultAction;
            navigate(`/phase05/depot-injections/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
      } else {
        const apiErrors = resultAction.payload;
        setErrors(apiErrors || {});
        Swal.fire({
          title: "Error",
          text: "Please correct the highlighted errors.",
          icon: "error",
        });
      }
    } catch (err) {
      console.error("Submission Error:", err);
      Swal.fire("Error", "Something went wrong while saving.", "error");
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="card">
          <div className="card-body">
            {Object.keys(errors).length > 0 && (
              <div className="alert alert-danger">
                {Object.entries(errors).map(([field, err], i) => (
                  <div key={i}>
                    {Array.isArray(err) ? err.join(", ") : `${field}: ${err}`}
                  </div>
                ))}
              </div>
            )}

            {/* ✅ Parent owns the <form> and handles submission */}
            <form onSubmit={handleSubmit}>
              <DepotInjectionForm
                formData={formData}
                setFormData={setFormData}
                isLoading={isLoading}
              />

              {/* Save Button */}
              <div className="col-12 d-flex justify-content-end mt-4">
                <div className="col-md-2 d-flex justify-content-end">
                  <button type="submit" className="btn btn-primary px-4 sticky-form-submit">
                    <i className="ri-save-2-line me-1"></i> Save
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddDepotInjection;
